import { c as _c } from "react/compiler-runtime";
import clsx from "classnames";
import { ContentSummaryTag } from "common/components/ContentSummaryTag";
import { Heading } from "common/components/Heading";
import { ImgixImage, ImgixImageProps } from "common/components/ImgixImage";
import { closestCrop } from "modules/editor/utils/closestCrop";
import { ImageAspectRatio } from "modules/editor/types";
import type { ContentSummaryVariantProps } from "common/components/ContentSummary/types";
import { Image, Maybe } from "common/types/graphql";
interface HeroSummaryImageProps {
  image?: Maybe<Partial<Image>>;
  imageSizes?: string;
  desktopImageAspectRatio: ImageAspectRatio;
  desktopImageCrop?: ImageAspectRatio;
}
const GradientOverlay = () => {
  "use memo";

  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div data-testid="gradient-overlay" className="absolute inset-0 bg-linear-to-t from-black from-20% to-60% opacity-85" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/**
 * `HeroSummaryImage` is a functional component that renders an image with a gradient overlay, given the image prop.
 * In case the image prop is undefined, the component will return null.
 * The component uses the `closestCrop` function to determine the best crop for the image based on the provided aspect ratio.
 * Widescreen desktopImageAspectRatio is no longer supported as it was causing double cropping. Since it is not a crop we collect.
 */
const HeroSummaryImage = t0 => {
  "use memo";

  const $ = _c(29);
  const {
    image,
    desktopImageAspectRatio,
    desktopImageCrop: t1,
    imageSizes
  } = t0;
  const desktopImageCrop = t1 === undefined ? desktopImageAspectRatio : t1;
  if (image?.src === undefined || desktopImageAspectRatio === ImageAspectRatio.Widescreen) {
    return null;
  }
  let t2;
  if ($[0] !== image || $[1] !== imageSizes) {
    t2 = {
      ...image,
      src: image.src,
      sizes: imageSizes
    };
    $[0] = image;
    $[1] = imageSizes;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const imageProps = t2;
  let t3;
  if ($[3] !== image.crops) {
    t3 = image.crops || [];
    $[3] = image.crops;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = closestCrop(ImageAspectRatio.Square, t3);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const squareCrop = t4;
  let t5;
  if ($[7] !== image.crops) {
    t5 = image.crops || [];
    $[7] = image.crops;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== desktopImageCrop || $[10] !== t5) {
    t6 = closestCrop(desktopImageCrop, t5);
    $[9] = desktopImageCrop;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const crop = t6;
  const t7 = desktopImageAspectRatio === ImageAspectRatio.Landscape;
  const t8 = desktopImageAspectRatio === ImageAspectRatio.Ultrawide;
  const t9 = desktopImageAspectRatio === ImageAspectRatio.Pano;
  let t10;
  if ($[12] !== t7 || $[13] !== t8 || $[14] !== t9) {
    t10 = clsx("absolute inset-0", {
      "sm:aspect-3/2": t7,
      "sm:aspect-3/1": t8,
      "sm:aspect-2/1": t9
    });
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== crop || $[17] !== image.alt || $[18] !== imageProps) {
    t11 = <div className="absolute inset-0 hidden sm:block"><ImgixImage {...imageProps} crop={crop} alt={image.alt} /></div>;
    $[16] = crop;
    $[17] = image.alt;
    $[18] = imageProps;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== image.alt || $[21] !== imageProps || $[22] !== squareCrop) {
    t12 = <div className="absolute inset-0 sm:hidden"><ImgixImage {...imageProps} crop={squareCrop} alt={image.alt} /></div>;
    $[20] = image.alt;
    $[21] = imageProps;
    $[22] = squareCrop;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <GradientOverlay />;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t12) {
    t14 = <><div className={t10}>{t11}{t12}{t13}</div></>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
};

/**
 * The `Hero` component is a variant of the `ContentSummary` component.
 * The summary image will use the crop based on the provided `desktopImageAspectRatio` prop
 * or square for small screens
 */
export const Hero = t0 => {
  "use memo";

  const $ = _c(30);
  const {
    title,
    url,
    byline,
    hideByline,
    contentType,
    imageSizes,
    image,
    desktopImageAspectRatio,
    desktopImageCrop,
    tagLabel,
    tagStyle,
    anchorTarget,
    className
  } = t0;
  const t1 = !desktopImageAspectRatio || desktopImageAspectRatio === ImageAspectRatio.Square;
  const t2 = desktopImageAspectRatio === ImageAspectRatio.Landscape;
  const t3 = desktopImageAspectRatio === ImageAspectRatio.Ultrawide;
  const t4 = desktopImageAspectRatio === ImageAspectRatio.Pano;
  let t5;
  if ($[0] !== className || $[1] !== t1 || $[2] !== t2 || $[3] !== t3 || $[4] !== t4) {
    t5 = clsx("group relative flex min-h-96 w-full flex-col justify-end outline-offset-4", className, {
      "sm:min-h-80 lg:min-h-96 xl:min-h-120": t1,
      "sm:aspect-3/2 sm:min-h-0": t2,
      "sm:aspect-3/1 sm:min-h-0": t3,
      "sm:aspect-2/1 sm:min-h-0": t4
    });
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== desktopImageAspectRatio || $[7] !== desktopImageCrop || $[8] !== image || $[9] !== imageSizes) {
    t6 = desktopImageAspectRatio && <HeroSummaryImage image={image} imageSizes={imageSizes} desktopImageAspectRatio={desktopImageAspectRatio} desktopImageCrop={desktopImageCrop} />;
    $[6] = desktopImageAspectRatio;
    $[7] = desktopImageCrop;
    $[8] = image;
    $[9] = imageSizes;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== contentType || $[12] !== tagLabel || $[13] !== tagStyle) {
    t7 = <ContentSummaryTag contentType={contentType} tagLabel={tagLabel} tagStyle={tagStyle} />;
    $[11] = contentType;
    $[12] = tagLabel;
    $[13] = tagStyle;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = title || "";
  let t9;
  if ($[15] !== t8) {
    t9 = <span className="drop-shadow-dark group-hover:underline"><Heading level={3}><span dangerouslySetInnerHTML={{
          __html: t8
        }} /></Heading></span>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== byline || $[18] !== hideByline) {
    t10 = byline && !hideByline && <p className="line-clamp-3 text-sm font-bold drop-shadow">{byline}</p>;
    $[17] = byline;
    $[18] = hideByline;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t7 || $[22] !== t9) {
    t11 = <section className="relative flex max-w-(--breakpoint-md) flex-col items-start gap-4 p-4 text-white md:p-8 xl:max-w-(--breakpoint-lg)">{t7}{t9}{t10}</section>;
    $[20] = t10;
    $[21] = t7;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== anchorTarget || $[25] !== t11 || $[26] !== t5 || $[27] !== t6 || $[28] !== url) {
    t12 = <article className="contents"><a href={url} target={anchorTarget} className={t5}>{t6}{t11}</a></article>;
    $[24] = anchorTarget;
    $[25] = t11;
    $[26] = t5;
    $[27] = t6;
    $[28] = url;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};